// src/hooks/useNavConfig.js
import { useEffect, useMemo, useState } from 'react';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { DEFAULT_ROLE_NAV, ROLE } from '../config/nav/defaultNav';

/**
 * Obtiene el rol principal del usuario desde AuthContext.
 * Soporta 'rol', 'mainRole' o 'role' por compatibilidad.
 */
const getUserMainRole = (user) => {
  const r = user?.rol || user?.mainRole || user?.role;
  return (r || ROLE.ATLETA).toString().toLowerCase();
};

/**
 * Merge de secciones por id:
 * - Si Firestore trae sección con mismo id, sobrescribe la default.
 * - Si el item tiene mismo 'to', se sobrescribe.
 */
const mergeSections = (baseSections = [], overrideSections = []) => {
  const map = new Map(baseSections.map(sec => [sec.id, { ...sec }]));
  overrideSections.forEach(sec => {
    if (!sec?.id) return;
    if (!map.has(sec.id)) {
      map.set(sec.id, { ...sec });
    } else {
      const base = map.get(sec.id);
      const itemsMap = new Map((base.items || []).map(i => [i.to, i]));
      (sec.items || []).forEach(i => itemsMap.set(i.to, i));
      map.set(sec.id, {
        ...base,
        ...sec,
        items: Array.from(itemsMap.values()),
      });
    }
  });
  return Array.from(map.values());
};

/**
 * Carga configuración de nav desde Firestore:
 * - nav_configs/{roleId}
 * - user_nav_overrides/{uid}
 * Si no existen, usa DEFAULT_ROLE_NAV[role].
 */
export const useNavConfig = (user) => {
  const db = useMemo(() => getFirestore(), []);
  const role = getUserMainRole(user);
  const [sections, setSections] = useState(DEFAULT_ROLE_NAV[role] || []);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      setLoading(true);
      try {
        const defaults = DEFAULT_ROLE_NAV[role] || [];

        // 1) Config a nivel rol
        const roleRef = doc(db, 'nav_configs', role);
        const roleSnap = await getDoc(roleRef);
        const roleSections = roleSnap.exists() ? (roleSnap.data()?.sections || []) : [];

        // 2) (Opcional) override por usuario
        let userSections = [];
        if (user?.uid) {
          const userRef = doc(db, 'user_nav_overrides', user.uid);
          const userSnap = await getDoc(userRef);
          userSections = userSnap.exists() ? (userSnap.data()?.sections || []) : [];
        }

        // Merge: defaults <- role <- user
        const merged = mergeSections(
          mergeSections(defaults, roleSections),
          userSections
        );

        if (isMounted) setSections(merged);
      } catch (e) {
        console.warn('useNavConfig: usando defaults por error/ausencia de Firestore.', e);
        if (isMounted) setSections(DEFAULT_ROLE_NAV[role] || []);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    load();
    return () => { isMounted = false; };
  }, [db, role, user?.uid]);

  return { sections, loading, role };
};
